import { webmcpTools, executeTool } from './tools.svelte.js';

/**
 * WebMCP integration for the appointment booking widget (Track A).
 *
 * Exposes the shared booking tool registry (see `tools.svelte.js`) to an
 * in-browser AI agent via WebMCP, so the agent can book an appointment in a
 * single call instead of walking the UI step by step. This module is only the
 * WebMCP *registration* layer — all booking logic, the tool definitions, and the
 * human-in-the-loop guarantees live in the shared registry and are reused by the
 * built-in Claude chat agent too.
 *
 * Uses the Chrome 150+ API: `document.modelContext.registerTool(def, { signal })`
 * with `execute` returning `{ content: [{ type: 'text', text }] }`.
 */

/**
 * Resolve the page-facing WebMCP object across Chrome versions.
 * Chrome 150+ exposes `document.modelContext`; earlier builds (and some
 * polyfills) expose `navigator.modelContext`.
 */
function getModelContext() {
    if (typeof document !== 'undefined' && document.modelContext) {
        return document.modelContext;
    }
    if (typeof navigator !== 'undefined' && navigator.modelContext) {
        return navigator.modelContext;
    }
    return null;
}

/**
 * Register the booking tools with the browser's WebMCP runtime.
 * No-ops in browsers without WebMCP support.
 * @returns {() => void} cleanup that unregisters every tool
 */
export function registerBookingTools() {
    const modelContext = getModelContext();

    if (!modelContext || typeof modelContext.registerTool !== 'function') {
        console.info('[webmcp] WebMCP API not available — no booking tools registered. Enable chrome://flags/#enable-webmcp-testing in Chrome 148+.');
        return () => {};
    }

    const controller = new AbortController();

    for (const tool of webmcpTools) {
        // Route execution through the shared, error-isolated executor so the
        // WebMCP runtime and the chat agent behave identically.
        const safeExecute = (input) => executeTool(tool.name, input);

        try {
            modelContext.registerTool(
                { ...tool, execute: safeExecute },
                { signal: controller.signal }
            );
        } catch (error) {
            console.error(`[webmcp] Failed to register tool "${tool.name}":`, error);
        }
    }

    console.info(`[webmcp] Registered ${webmcpTools.length} booking tools.`);

    return () => controller.abort();
}
